





















































import Vue, { PropType } from 'vue'
import { debounce } from 'debounce'
import Person from '@/types/Person.interface'
import Broadcast from '@/types/Broadcast.interface'
import MessagePreviewBubble from '@/components/MessagePreviewBubble.vue'
import PersonSearchResults from '@/components/PersonSearchResults.vue'
import UserLogo from '@/components/UserLogo.vue'
import { randomKey } from '@/utility/helpers'
import User from '@/types/User.interface'
export default Vue.extend({
    components: { MessagePreviewBubble, PersonSearchResults, UserLogo },
    props: {
        broadcast: {
            type: Object as PropType<Broadcast>,
            required: true
        },
        companyToggle: {
            type: Boolean,
            default: false
        },
        consentToggle: {
            type: Boolean,
            default: false
        },
        //  We pass the boolean checks from BroadcastForm.vue to see if consent is already in the message to reduce repeat code and prevent double consent appearing in the message
        hasConsent: {
            type: Boolean, 
            default: true
        },
        //  We pass the boolean checks from BroadcastForm.vue to see if company name is already in the message to reduce repeat code and prevent double company name appearing in the message
        hasCompanyName: {
            type: Boolean, 
            default: true
        }
    },
    data: () => ({
        loading: false,
        personExpand: false,
        personList: [],
        selectedPerson: null
    }),
    async mounted() {
        //  Grab our user id
        const userId = this.$store.getters.user.id

        //  We need to get our user's person
        this.selectedPerson = await this.$store.dispatch('usersPersonFind', userId)
    },
    computed: {
        broadcastMessage(): string {
            //  Grab our broadcast message
            let msg = this.broadcast.message || ''

            //  If we have our company toggled and it doesn't have a company name yet we append it, this logic prevents a case where a draft or scheduled broadcast when editied appends additonal
            //  company names over and over again for each edit
            if (this.companyToggle && !this.hasCompanyName) {
                msg = `${this.companyName}${msg}`
            }
            //  If we have our consent message toggled and don't have our consent message in the broadcast, append it, same reasoning as above
            if (this.consentToggle && !this.hasConsent) {
                msg = `${msg}${this.consentMsg}`
            }

            //  In the case the user is editing their message from a schedule or draft broadcast we need the inverse logic of above so that the broadcast preview removes the text if they
            //  turn one of the toggles off
            if (!this.companyToggle && this.hasCompanyName) {
                msg = msg.replace(this.companyName, '')
            }
            if (!this.consentToggle && this.hasConsent) {
                msg = msg.replace(this.consentMsg, '')
            }

            //  Return our message
            return msg
        },
        companyName(): string {
            //  Grab the current tenant from the store
            const tenant = this.$store.getters.tenant

            //  Get our tenant's organization name
            const orgName = tenant.company_name

            //  Return the orgname with a newline appended
            return `${orgName}\n`
        },
        consentMsg(): string {
            //  Grab our consent from the store
            const consent = this.$store.getters.consent

            //  Grab the opt in message
            const optIn = consent?.opt_in_message

            //  Return the opt in with a newline prepended
            return `\n${optIn}`
        },
        filteredPersons(): Person[] {
            //  Grab our persons
            const persons = this.personList

            //  If there aren't any, return empty array
            if (!persons) return []

            //  else, filter out any users
            const noUsers = persons.filter((x: Person) => !x.user_id)

            //  Remove opt outs
            const noOptOuts = noUsers.filter(
                ({ phone_default }) => !this.optedOut.includes(phone_default?.msisdn)
            )

            //  Get our exclusion persons
            const exclude = this.excludePersons || []

            //  Remove our excluded persons
            const filtered = noOptOuts.filter(({ id }) => !exclude.includes(id))

            //  Return our filtered persons
            return filtered
        },
        optedOut(): number[] {
            return this.$store.getters.optedOut
        },
        searchProp: {
            get() {
                return this.searchtext
            },
            set: debounce(function (value: string) {
                //  Set our search text to keep track of it
                this.searchtext = value

                //  Search for both persons and groups
                this.search()
            }, 750)
        },
        user(): User {
            return this.$store.getters.user
        }
    },
    methods: {
        onPersonRemoved(): void {
            //  Remove our selected person
            this.selectedPerson = null
        },
        onPersonSelected(person: Person): void {
            //  Set our selected person
            this.selectedPerson = person

            //  Set our search text to null
            this.searchtext = null

            //  Reset our lists
            this.personExpand = false
            this.personList = null
        },
        personName(name: string): string {
            //  If it doesn't exist just return an empty string
            if (!name) return ''

            //  If our name is 15 characters or less, just return the name
            if (name.length <= 15) return name

            //  Else, concatenate and append ellipses
            return name.substring(0, 15) + '...'
        },
        async personsSearch(): Promise<void> {
            //  If search text does NOT exist, set searched to false
            if (!this.searchtext) {
                this.personExpand = false
                this.personList = null
                return
            }

            //  Else, assemble the search payload
            const payload = { searchtext: this.searchtext, limit: 30 }

            //  Expand our list
            this.personExpand = true

            //  Else, sent the search request to the API
            const persons = await this.$store.dispatch('personsSearch', payload)

            //  Set our persons
            this.personList = persons
        },
        async search(): Promise<void> {
            //  Start our loading state
            this.loading = true

            await this.personsSearch()

            //  Stop loading
            this.loading = false

            //  Open or close our Person search based on number of results
            this.personExpand = this.personList.length > 0
        },
        async submit(): Promise<void> {
            //  Start loading
            this.loading = true

            //  Create a payload for sending messages
            const payload = {
                channel_id: this.selectedPerson.primary_channel_id,
                text: this.broadcastMessage,
                media_id: this.broadcast.media_id,
                outbound: 1,
                type: 'test'
            }

            //  Send our message
            const message = await this.$store.dispatch('messagesPost', payload)

            //  Regardless of result lets stop loading
            this.loading = false

            //  If it failed do nothing
            if (!message) return

            //  If it worked lets close
            this.$store.dispatch('dialogClose', 'APP')

            //  Else, notify of success
            this.$store.commit('notificationPopupOpen', {
                title: 'Test Message Sent',
                icon: 'bi-chat-square-dots-fill',
                id: randomKey()
            })
        }
    }
})
